import { SAFE_ZONE_STRINGS } from '@src/safe-zones/strings';
import LightbulbIcon from '@src/svgs/LightbulbIcon';
import { forwardRef } from 'react';
import styled from 'styled-components';

const COPY = SAFE_ZONE_STRINGS.hint;

export interface SafeZoneHintProps {
  /**
   * Short label for the region the hint is attached to (e.g. "Captions",
   * "Player Controls"). Surfaced as the title above the description.
   */
  title?: string;
  /**
   * What this region of the overlay is or does. Rendered as a bulleted list —
   * one bullet per entry — so the hint stays scannable.
   */
  description: readonly string[];
  /** One or more "Pro Tip" lines surfaced below the description. */
  proTips?: readonly string[];
}

/**
 * Rich hover tooltip body shown when a creator hovers a variant pill in the
 * Safe Zone toolbar. Renders the variant's title, description, and any pro
 * tips. `forwardRef` because consumers (e.g. react-tooltip) may need to track
 * the body's bounding rect.
 */
export const SafeZoneHintTooltipContent = forwardRef<
  HTMLDivElement,
  SafeZoneHintProps
>(function SafeZoneHintTooltipContent(
  { title, description, proTips = [] },
  ref,
) {
  return (
    <Container ref={ref}>
      {title && <Title>{title}</Title>}
      <SectionHeader>{COPY.descriptionHeader}</SectionHeader>
      <BulletList>
        {description.map((line, idx) => (
          <BulletRow key={idx}>
            <Bullet aria-hidden>•</Bullet>
            <Body>{line}</Body>
          </BulletRow>
        ))}
      </BulletList>

      {proTips.length > 0 && (
        <>
          <Divider />
          <SectionHeader>
            {proTips.length === 1 ? COPY.proTipSingular : COPY.proTipPlural}
          </SectionHeader>
          {proTips.map((tip, idx) => (
            <ProTipRow key={idx}>
              <ProTipIcon>
                <LightbulbIcon
                  width="12"
                  height="12"
                  fillColor="var(--accent)"
                />
              </ProTipIcon>
              <Body>{tip}</Body>
            </ProTipRow>
          ))}
        </>
      )}
    </Container>
  );
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  min-width: 200px;
  max-width: 280px;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--elevation-toast);
  /* Interactive so users can mouse into the tooltip and select / copy text. */
  pointer-events: auto;
  user-select: text;
`;

const Title = styled.div`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  color: var(--foreground);
  letter-spacing: 0.01em;
  padding-bottom: var(--spacing-2xs);
  margin-bottom: var(--spacing-2xs);
  border-bottom: 1px solid var(--border);
`;

const SectionHeader = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-2xs);
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  color: var(--text-muted);
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const BulletList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2xs);
`;

const BulletRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs);
`;

const Bullet = styled.span`
  flex: 0 0 auto;
  /* Optical alignment with the first line of body copy. */
  line-height: 1.4;
  color: var(--text-muted);
  font-size: 12px;
  /* Bullets are decorative — let pointer events fall through. */
  user-select: none;
`;

const Body = styled.div`
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
  opacity: 0.82;
  line-height: 1.4;
`;

const Divider = styled.div`
  height: 1px;
  background: var(--border);
  margin: var(--spacing-2xs) 0;
`;

const ProTipRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs);
`;

const ProTipIcon = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  /* Optical alignment with the first line of body copy. */
  margin-top: 2px;
  line-height: 0;
`;
